import type { UpcomingPayments as UpcomingPaymentsData } from '../../lib/dashboardApi';
import { formatCurrency, formatDate } from '../../lib/format';

interface UpcomingPaymentsProps {
  data: UpcomingPaymentsData;
}

/**
 * Upcoming payments & receivables (CLE-256, Secțiunea 3). Chronological timeline
 * for the next N days, green = income, red = outcome, with a net summary.
 */
export function UpcomingPayments({ data }: UpcomingPaymentsProps) {
  const netPositive = data.netTotal >= 0;
  return (
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          Plăți & încasări — următoarele {data.days} zile
        </h3>
        <span
          className={`text-sm font-semibold ${netPositive ? 'text-emerald-400' : 'text-red-400'}`}
        >
          {netPositive ? '+' : '−'}
          {formatCurrency(Math.abs(data.netTotal), data.currency)} net
        </span>
      </div>

      <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
        <span>
          Încasări:{' '}
          <span className="font-medium text-emerald-400">
            {formatCurrency(data.totalIncome, data.currency)}
          </span>
        </span>
        <span>
          Plăți:{' '}
          <span className="font-medium text-red-400">
            {formatCurrency(data.totalOutcome, data.currency)}
          </span>
        </span>
      </div>

      {data.items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">
          Nicio scadență în următoarele {data.days} zile.
        </p>
      ) : (
        <ul className="mt-4 space-y-1">
          {data.items.map((item) => {
            const income = item.type === 'income';
            return (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/40"
              >
                <span
                  className={`h-8 w-1 shrink-0 rounded-full ${income ? 'bg-emerald-500' : 'bg-red-500'}`}
                  aria-hidden
                />
                {/* The date gets its own column only where there is room for it;
                    on phones it moves under the name so the name stays readable. */}
                <div className="hidden w-24 shrink-0 text-xs text-muted-foreground sm:block">
                  {formatDate(item.dueDate)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">
                    {item.party ?? item.invoiceNumber}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    <span className="sm:hidden">{formatDate(item.dueDate)} · </span>
                    {item.invoiceNumber}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-sm font-semibold ${income ? 'text-emerald-400' : 'text-red-400'}`}
                >
                  {income === item.amount >= 0 ? '+' : '−'}
                  {formatCurrency(Math.abs(item.amount), item.currency)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
